import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';

import { errorText } from './labels';

// "Continue on another device" (plan §6, D8/D11): the holder gives the link up — `release` clears the
// claim and the draft and kills the session — and the page shows the link and its QR code for the
// other device.

interface DialogProps {
    open: boolean;
    onClose: () => void;
    /** Releases the claim; rejects with the API error. */
    onRelease: () => Promise<void>;
}

export function ContinueElsewhereDialog({ open, onClose, onRelease }: DialogProps) {
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    React.useEffect(() => {
        if (open) {
            setError(null);
            setBusy(false);
        }
    }, [open]);

    const release = async () => {
        setBusy(true);
        setError(null);
        try {
            await onRelease();
        } catch (e) {
            setError(e);
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} aria-labelledby="signup-release-title">
            <DialogTitle id="signup-release-title">Continue on another device?</DialogTitle>
            <DialogContent>
                <DialogContentText paragraph>
                    This browser gives up the link, and you open it on the other device instead.
                </DialogContentText>
                <DialogContentText>
                    What you entered here is not kept: on the other device you start again by connecting your booking
                    system.
                </DialogContentText>
                {!!error && <Alert severity="error" sx={{ mt: 2 }}>{errorText(error)}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={release} disabled={busy}
                    startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}>
                    Continue on another device
                </Button>
            </DialogActions>
        </Dialog>
    );
}

interface ReleasedProps {
    link: string;
    onStartAgain: () => void;
}

export function Released({ link, onStartAgain }: ReleasedProps) {
    const [copied, setCopied] = React.useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
        } catch (_e) {
            setCopied(false);
        }
    };
    return (
        <Box>
            <Typography variant="h5" component="h1" gutterBottom>Open the link on your other device</Typography>
            <Typography color="text.secondary" paragraph>
                Scan the code with the other device&apos;s camera, or copy the link and open it there.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <Box sx={{ p: 2, bgcolor: '#fff', border: 1, borderColor: 'divider', borderRadius: 1, lineHeight: 0 }}>
                    <QRCodeSVG value={link} size={192} includeMargin={false} data-testid="signup-release-qr" />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                    label="Your link"
                    value={link}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    inputProps={{ 'data-testid': 'signup-release-link' }}
                    onFocus={(e) => e.target.select()}
                />
                <Button variant="outlined" onClick={copy} sx={{ whiteSpace: 'nowrap' }}>{copied ? 'Copied' : 'Copy link'}</Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Keep the link to yourself: whoever opens it first can set up the account.
            </Typography>
            <Box sx={{ mt: 2 }}>
                <Button onClick={onStartAgain}>Continue in this browser after all</Button>
            </Box>
        </Box>
    );
}
